import { ReactNode, useLayoutEffect } from 'react';
import { CacheProvider } from '@emotion/react';
import { GlobalStyles } from '@mui/material';
import { useAppSelector } from '../store/types';
import { selectIsDarkMode } from '../store/reducers/uiReducer';
import { theme } from '../theme';
import { darkenCssValue } from './darkColors';
import { darkEmotionCache, lightEmotionCache } from './emotionCaches';
import { startInlineDarkener, stopInlineDarkener } from './inlineDarkener';

// App-wide light/dark switch (ui.themeMode, persisted as localStorage
// `app_ui_theme_mode`, light by default). The MUI theme itself stays the light
// theme: in dark mode every color is converted where it is emitted (see
// darkColors.ts), which is what lets ~6,000 hardcoded colors follow along.
// Keep the CacheProvider mounted in both modes — swapping its `value` re-renders
// the styled tree; wrapping conditionally would remount the app and drop state.

export const DARK_PAGE_BACKGROUND = darkenCssValue(theme.palette.background.default, 'bg');

// Rendered through the dark cache, so anything here must already be dark-safe.
const darkGlobals = (
  <GlobalStyles
    styles={{
      // The MAVI wordmark is a dark SVG on every sidebar and launcher; inverting
      // and rotating the hue back keeps the brand color and makes it readable.
      'img[src*="mavi%20logo.svg"], img[src*="mavi logo.svg"]': {
        filter: 'invert(1) hue-rotate(180deg)',
      },
      // X "Lights out" touches. Floating menus get its soft white glow: the theme
      // strips Paper shadows, and a black menu over a black page needs an edge.
      '.MuiPopover-paper, .MuiAutocomplete-paper, .MuiPickerPopper-paper, .MuiPickersPopper-paper': {
        boxShadow: 'rgba(255, 255, 255, 0.2) 0px 0px 15px, rgba(255, 255, 255, 0.15) 0px 0px 3px 1px !important',
      },
      // Modal scrims use its blue-gray instead of black.
      '.MuiBackdrop-root:not(.MuiBackdrop-invisible)': {
        backgroundColor: 'rgba(91, 112, 131, 0.4) !important',
      },
    }}
  />
);

export default function ColorModeRoot({ children }: { children: ReactNode }) {
  const dark = useAppSelector(selectIsDarkMode);

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.colorMode = dark ? 'dark' : 'light';
    // Native scrollbars, date inputs and select popups follow this.
    root.style.colorScheme = dark ? 'dark' : 'light';
    root.style.backgroundColor = dark ? DARK_PAGE_BACKGROUND : '';
    if (!dark) return undefined;
    startInlineDarkener();
    return () => stopInlineDarkener();
  }, [dark]);

  return (
    <CacheProvider value={dark ? darkEmotionCache : lightEmotionCache}>
      {children}
      {dark && darkGlobals}
    </CacheProvider>
  );
}
